export interface EnvironmentElement {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  radius?: number;
  opacity?: number;
  zIndex?: number;
}

export interface WallElement {
  id: string;
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
  w: number;
  wCalc?: string; // optional CSS calc() override for width (e.g. 'calc(50% - 26px)')
  h: number;
  color: string;
  zIndex?: number;
  radius?: number;
}

export interface MapConfig {
  id: string;
  name: string;
  mapCode: string;
  siteName: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Extreme';
  targetStyle: string;

  // Timing
  windowMs: number;

  // Spawn config
  spawnSide: 'left' | 'right';
  // wallSide: which side of the frame the wall/corner sits on.
  // This drives the automatic swing-out direction per angle.
  // 'left' = wall is on the left → target swings left-to-right into view
  // 'right' = wall is on the right → target swings right-to-left into view
  wallSide: 'left' | 'right';
  spawnX: number; // % from left
  spawnY: number; // % from bottom
  spawnYOffsetPx: number; // px to raise target above spawnY so head hitbox center aligns with crosshair y
  crosshairOffsetX: number; // % adjustment to align crosshair with head
  crosshairOffsetY: number; // % adjustment

  // Optional background image — when set, replaces procedural scene art
  backgroundImage?: string;

  // Scene colors
  skyGradient: string;
  floorGradient: string;
  floorHeightPercent: number;
  previewGradient: string;
  floorColor: string;
  wallColor: string;
  accentColor: string;
  ceilingColor: string;

  // Environment & wall geometry
  environmentElements: EnvironmentElement[];
  wallElements: WallElement[];

  // Target appearance
  targetHeadColor: string;
  targetHeadBorder: string;
  targetBodyColor: string;
  targetBodyBorder: string;
  targetEyeColor: string;

  // Gun model colors
  gunBodyColor: string;
  gunBarrelColor: string;
  gunAccentColor: string;
  gunDetailColor: string;
}

// Backend integration point: map configs could be fetched from CMS/API
export const MAP_CONFIGS: MapConfig[] = [
  {
    id: 'map-sunset-b',
    name: 'Sunset B-Site',
    mapCode: 'SUNSET',
    siteName: 'B Site',
    description: 'Warm desert tones with a tight corner peek. Target swings from the right side of the orange-lit archway.',
    difficulty: 'Easy',
    targetStyle: 'Standard',
    windowMs: 600,
    spawnSide: 'right',
    wallSide: 'right',
    spawnX: 58,
    spawnY: 22,
    spawnYOffsetPx: 38,
    crosshairOffsetX: 1,
    crosshairOffsetY: 10,
    skyGradient: 'linear-gradient(180deg, #FF8C42 0%, #FFB347 30%, #FFD580 60%, #FFF4CC 100%)',
    floorGradient: 'linear-gradient(180deg, #C4A882 0%, #B89060 100%)',
    floorHeightPercent: 28,
    previewGradient: 'linear-gradient(135deg, #FF8C42 0%, #C4A882 100%)',
    floorColor: '#B89060',
    wallColor: '#C4A882',
    accentColor: '#E87C3E',
    ceilingColor: '#8B6340',
    environmentElements: [
      { id: 'sun-glow', x: 60, y: 5, w: 20, h: 20, color: 'radial-gradient(circle, rgba(255,255,200,0.4) 0%, transparent 70%)', opacity: 0.8, zIndex: 1 },
      { id: 'distant-building-1', x: 55, y: 15, w: 25, h: 45, color: '#D4B896', zIndex: 2 },
      { id: 'distant-building-2', x: 75, y: 25, w: 20, h: 35, color: '#C9A87A', zIndex: 2 },
      { id: 'palm-trunk', x: 65, y: 20, w: 2, h: 35, color: '#6B4A2A', zIndex: 3 },
      { id: 'palm-top', x: 60, y: 12, w: 12, h: 14, color: '#4A7A3A', radius: 50, zIndex: 3 },
      { id: 'ground-shadow', x: 0, y: 70, w: 100, h: 8, color: 'rgba(0,0,0,0.15)', zIndex: 4 },
    ],
    wallElements: [
      { id: 'left-wall-main', left: 0, top: 0, w: 42, h: 100, color: '#C4A882', zIndex: 5 },
      { id: 'left-wall-edge', left: 42, top: 0, w: 1.5, h: 100, color: '#E87C3E', zIndex: 6 },
      { id: 'left-wall-shadow', left: 43, top: 0, w: 4, h: 100, color: 'rgba(0,0,0,0.25)', zIndex: 6 },
      { id: 'ceiling-beam', left: 0, top: 0, w: 100, h: 8, color: '#8B6340', zIndex: 7 },
      { id: 'window-frame', left: 12, top: 25, w: 18, h: 28, color: '#7A5530', zIndex: 7 },
      { id: 'window-bars', left: 14, top: 27, w: 14, h: 24, color: '#4A3520', zIndex: 8 },
      { id: 'floor-trim', left: 0, bottom: 28, w: 42, h: 3, color: '#9A7250', zIndex: 7 },
    ],
    targetHeadColor: '#D4C4B0',
    targetHeadBorder: '#FF4655',
    targetBodyColor: '#B8A090',
    targetBodyBorder: '#8A7060',
    targetEyeColor: '#2A1A0A',
    gunBodyColor: '#2A2A2A',
    gunBarrelColor: '#1A1A1A',
    gunAccentColor: '#3A3A3A',
    gunDetailColor: '#555555',
  },
  {
    id: 'map-ascent-b',
    name: 'Ascent B-Site',
    mapCode: 'ASCENT',
    siteName: 'B Site',
    description: 'Italian cobblestone alleyways. Target peeks from the left archway entrance with a narrow exposure window.',
    difficulty: 'Medium',
    targetStyle: 'Slim',
    windowMs: 500,
    spawnSide: 'left',
    wallSide: 'left',
    spawnX: 38,
    spawnY: 20,
    spawnYOffsetPx: 38,
    crosshairOffsetX: -1,
    crosshairOffsetY: 9,
    skyGradient: 'linear-gradient(180deg, #7BB8E8 0%, #A8CFF0 40%, #D4E8F8 80%, #EEF5FC 100%)',
    floorGradient: 'linear-gradient(180deg, #7A7060 0%, #5A5248 100%)',
    floorHeightPercent: 30,
    previewGradient: 'linear-gradient(135deg, #C4865A 0%, #7A7060 100%)',
    floorColor: '#5A5248',
    wallColor: '#C4865A',
    accentColor: '#8A6040',
    ceilingColor: '#7A5030',
    environmentElements: [
      { id: 'sky-base', x: 0, y: 0, w: 100, h: 70, color: 'transparent', zIndex: 0 },
      { id: 'distant-tower', x: 40, y: 10, w: 8, h: 40, color: '#C4A882', zIndex: 2 },
      { id: 'tower-top', x: 39, y: 6, w: 10, h: 8, color: '#B89870', zIndex: 2 },
      { id: 'vine-left', x: 28, y: 5, w: 3, h: 25, color: '#4A7A3A', opacity: 0.7, zIndex: 3 },
      { id: 'lamp-post', x: 48, y: 20, w: 1, h: 30, color: '#5A4A30', zIndex: 3 },
      { id: 'lamp-head', x: 46, y: 17, w: 5, h: 6, color: '#8A7050', radius: 2, zIndex: 3 },
      { id: 'cobble-detail-1', x: 0, y: 72, w: 30, h: 4, color: 'rgba(0,0,0,0.1)', zIndex: 4 },
      { id: 'cobble-detail-2', x: 35, y: 75, w: 40, h: 4, color: 'rgba(0,0,0,0.1)', zIndex: 4 },
    ],
    wallElements: [
      { id: 'right-wall-main', right: 0, top: 0, w: 38, h: 100, color: '#C4865A', zIndex: 5 },
      { id: 'right-wall-edge', right: 38, top: 0, w: 1.5, h: 100, color: '#6A4020', zIndex: 6 },
      { id: 'right-wall-shadow', right: 39.5, top: 0, w: 5, h: 100, color: 'rgba(0,0,0,0.3)', zIndex: 6 },
      { id: 'arch-frame', right: 8, top: 5, w: 22, h: 65, color: '#B07848', zIndex: 7 },
      { id: 'arch-inner', right: 10, top: 8, w: 18, h: 60, color: '#D4A070', zIndex: 8 },
      { id: 'arch-keystone', right: 17, top: 5, w: 4, h: 10, color: '#8A6040', radius: 2, zIndex: 9 },
      { id: 'shutter', right: 5, top: 35, w: 12, h: 18, color: '#7A6040', zIndex: 7 },
      { id: 'wall-trim-top', right: 0, top: 0, w: 38, h: 5, color: '#7A5030', zIndex: 8 },
    ],
    targetHeadColor: '#C8B89A',
    targetHeadBorder: '#FF4655',
    targetBodyColor: '#A09080',
    targetBodyBorder: '#706050',
    targetEyeColor: '#1A0A00',
    gunBodyColor: '#1A1205',
    gunBarrelColor: '#0A0800',
    gunAccentColor: '#C8A020',
    gunDetailColor: '#FFD040',
  },
  {
    id: 'map-lotus-c',
    name: 'Lotus C-Garage',
    mapCode: 'LOTUS',
    siteName: 'C Garage',
    description: 'Ancient Bhutanese architecture with teal tech doors. Tight garage entry — target peeks from the right door frame.',
    difficulty: 'Hard',
    targetStyle: 'Fast',
    windowMs: 450,
    spawnSide: 'right',
    wallSide: 'right',
    spawnX: 60,
    spawnY: 20,
    spawnYOffsetPx: 38,
    crosshairOffsetX: 0,
    crosshairOffsetY: 9,
    skyGradient: 'linear-gradient(180deg, #1A1A2E 0%, #2A2A4E 50%, #3A2A3E 100%)',
    floorGradient: 'linear-gradient(180deg, #4A4A5A 0%, #2A2A38 100%)',
    floorHeightPercent: 32,
    previewGradient: 'linear-gradient(135deg, #2A2A4E 0%, #4A4A5A 100%)',
    floorColor: '#2A2A38',
    wallColor: '#D4C8B0',
    accentColor: '#00CCAA',
    ceilingColor: '#6A4A2A',
    environmentElements: [
      { id: 'ceiling-tiles', x: 0, y: 0, w: 100, h: 15, color: '#3A2A1A', zIndex: 2 },
      { id: 'ceiling-beam-1', x: 10, y: 0, w: 4, h: 15, color: '#5A3A1A', zIndex: 3 },
      { id: 'ceiling-beam-2', x: 45, y: 0, w: 4, h: 15, color: '#5A3A1A', zIndex: 3 },
      { id: 'ceiling-beam-3', x: 80, y: 0, w: 4, h: 15, color: '#5A3A1A', zIndex: 3 },
      { id: 'string-lights', x: 5, y: 8, w: 90, h: 1, color: 'rgba(255,240,180,0.4)', zIndex: 4 },
      { id: 'light-bulb-1', x: 15, y: 6, w: 2, h: 4, color: 'rgba(255,240,150,0.8)', radius: 50, zIndex: 4 },
      { id: 'light-bulb-2', x: 35, y: 7, w: 2, h: 4, color: 'rgba(255,240,150,0.8)', radius: 50, zIndex: 4 },
      { id: 'light-bulb-3', x: 55, y: 6, w: 2, h: 4, color: 'rgba(255,240,150,0.8)', radius: 50, zIndex: 4 },
      { id: 'light-bulb-4', x: 75, y: 7, w: 2, h: 4, color: 'rgba(255,240,150,0.8)', radius: 50, zIndex: 4 },
      { id: 'shelf-left', x: 2, y: 30, w: 15, h: 5, color: '#6A5030', zIndex: 5 },
      { id: 'barrel-left', x: 5, y: 35, w: 6, h: 10, color: '#8A6A40', radius: 2, zIndex: 5 },
      { id: 'box-left', x: 3, y: 42, w: 10, h: 10, color: '#7A6040', zIndex: 5 },
      { id: 'ornament-top', x: 30, y: 8, w: 12, h: 8, color: '#B84A2A', zIndex: 5 },
      { id: 'ornament-pattern', x: 32, y: 9, w: 8, h: 6, color: '#C86A30', zIndex: 6 },
    ],
    wallElements: [
      { id: 'back-wall', left: 0, top: 0, w: 100, h: 100, color: '#E8DCC8', zIndex: 1 },
      { id: 'door-frame-outer', left: 35, top: 12, w: 30, h: 72, color: '#6A5030', zIndex: 6 },
      { id: 'door-frame-inner', left: 37, top: 14, w: 26, h: 68, color: '#2A2A3A', zIndex: 7 },
      { id: 'door-left-panel', left: 37, top: 14, w: 12, h: 68, color: '#1A1A2A', zIndex: 8 },
      { id: 'door-right-panel', left: 51, top: 14, w: 12, h: 68, color: '#1A1A2A', zIndex: 8 },
      { id: 'door-teal-strip-1', left: 37, top: 25, w: 26, h: 2, color: '#00CCAA', zIndex: 9 },
      { id: 'door-teal-strip-2', left: 37, top: 45, w: 26, h: 2, color: '#00CCAA', zIndex: 9 },
      { id: 'door-teal-strip-3', left: 37, top: 65, w: 26, h: 2, color: '#00CCAA', zIndex: 9 },
      { id: 'right-wall-cover', right: 0, top: 0, w: 28, h: 100, color: '#D4C8B0', zIndex: 5 },
      { id: 'right-wall-edge', right: 28, top: 0, w: 2, h: 100, color: '#8A7050', zIndex: 6 },
    ],
    targetHeadColor: '#C0B098',
    targetHeadBorder: '#FF4655',
    targetBodyColor: '#A09080',
    targetBodyBorder: '#706050',
    targetEyeColor: '#1A0A00',
    gunBodyColor: '#1A1A0A',
    gunBarrelColor: '#0A0A05',
    gunAccentColor: '#C89020',
    gunDetailColor: '#AA2020',
  },
  {
    id: 'map-pearl-a',
    name: 'Pearl A-Site',
    mapCode: 'PEARL',
    siteName: 'A Site',
    description: 'Underwater Lisbon-inspired ruins. Extreme difficulty — target peeks from a narrow stone archway with minimal exposure.',
    difficulty: 'Extreme',
    targetStyle: 'Nano',
    windowMs: 400,
    spawnSide: 'left',
    wallSide: 'left',
    spawnX: 32,
    spawnY: 22,
    spawnYOffsetPx: 38,
    crosshairOffsetX: -1,
    crosshairOffsetY: 10,
    skyGradient: 'linear-gradient(180deg, #1A3A5A 0%, #2A4A6A 30%, #3A5A7A 60%, #4A6A8A 100%)',
    floorGradient: 'linear-gradient(180deg, #5A6A7A 0%, #3A4A5A 100%)',
    floorHeightPercent: 30,
    previewGradient: 'linear-gradient(135deg, #1A3A5A 0%, #5A6A7A 100%)',
    floorColor: '#3A4A5A',
    wallColor: '#8A9AA0',
    accentColor: '#4A8ACA',
    ceilingColor: '#2A3A4A',
    environmentElements: [
      { id: 'water-shimmer', x: 0, y: 0, w: 100, h: 100, color: 'rgba(30,80,140,0.08)', zIndex: 1 },
      { id: 'distant-arch-1', x: 40, y: 20, w: 15, h: 45, color: '#6A7A80', zIndex: 2 },
      { id: 'distant-arch-2', x: 60, y: 30, w: 12, h: 35, color: '#5A6A70', zIndex: 2 },
      { id: 'mossy-patch-1', x: 0, y: 45, w: 8, h: 12, color: '#3A5A3A', opacity: 0.6, zIndex: 3 },
      { id: 'mossy-patch-2', x: 85, y: 50, w: 10, h: 10, color: '#3A5A3A', opacity: 0.5, zIndex: 3 },
      { id: 'crack-1', x: 25, y: 30, w: 0.5, h: 20, color: 'rgba(0,0,0,0.3)', zIndex: 4 },
      { id: 'crack-2', x: 70, y: 25, w: 0.5, h: 15, color: 'rgba(0,0,0,0.3)', zIndex: 4 },
      { id: 'light-ray-1', x: 35, y: 0, w: 8, h: 60, color: 'rgba(100,180,255,0.06)', zIndex: 1 },
      { id: 'light-ray-2', x: 55, y: 0, w: 6, h: 50, color: 'rgba(100,180,255,0.04)', zIndex: 1 },
    ],
    wallElements: [
      { id: 'left-wall-stone', left: 0, top: 0, w: 30, h: 100, color: '#8A9AA0', zIndex: 5 },
      { id: 'left-wall-edge', left: 30, top: 0, w: 2, h: 100, color: '#2A4A6A', zIndex: 6 },
      { id: 'left-shadow', left: 32, top: 0, w: 6, h: 100, color: 'rgba(0,0,0,0.35)', zIndex: 6 },
      { id: 'stone-arch-left', left: 4, top: 15, w: 18, h: 55, color: '#7A8A90', zIndex: 7 },
      { id: 'arch-keystone', left: 11, top: 11, w: 4, h: 8, color: '#6A7A80', radius: 2, zIndex: 8 },
      { id: 'wall-texture-1', left: 2, top: 20, w: 6, h: 3, color: 'rgba(0,0,0,0.1)', zIndex: 8 },
      { id: 'wall-texture-2', left: 10, top: 35, w: 8, h: 2, color: 'rgba(0,0,0,0.1)', zIndex: 8 },
      { id: 'wall-texture-3', left: 3, top: 50, w: 5, h: 3, color: 'rgba(0,0,0,0.1)', zIndex: 8 },
      { id: 'ceiling-stone', left: 0, top: 0, w: 100, h: 10, color: '#2A3A4A', zIndex: 9 },
    ],
    targetHeadColor: '#B8C8D0',
    targetHeadBorder: '#FF4655',
    targetBodyColor: '#8A9AA0',
    targetBodyBorder: '#5A6A70',
    targetEyeColor: '#0A1A2A',
    gunBodyColor: '#1A2A3A',
    gunBarrelColor: '#0A1A2A',
    gunAccentColor: '#2A5A9A',
    gunDetailColor: '#4A8ACA',
  },
];

// Icebox map config — exported separately as the default/featured map
export const ICEBOX_MAP: MapConfig = {
  id: 'map-icebox-b',
  name: 'Icebox B-Site',
  mapCode: 'ICEBOX',
  siteName: 'B Site',
  description: 'Frozen industrial site. Target peeks from the narrow gap between the KNG container stack and the adjacent wall.',
  difficulty: 'Hard',
  targetStyle: 'Fast',
  windowMs: 450,
  // spawnSide 'left' → the code sets right: (100 - spawnX)% on the target div,
  // meaning the target's RIGHT edge sits at spawnX% from the left.
  // Container edge is at left: 38% + w: 2% = 40%.
  // spawnX: 42 → right edge at 42% from left = 2% past the wall edge, tight peek.
  spawnSide: 'left',
  wallSide: 'left',
  spawnX: 42,
  // spawnY: 34 → target bottom at 34% from bottom (66% from top).
  // Head center ≈ 66% - (124/vh)% + (18/vh)% ≈ 50% for a ~600px viewport — aligns with crosshair.
  spawnY: 34,
  spawnYOffsetPx: 38,
  crosshairOffsetX: 0,
  crosshairOffsetY: 9,
  skyGradient: 'linear-gradient(180deg, #8A9BAD 0%, #A8B8C8 35%, #C4D0D8 65%, #D8E0E6 100%)',
  floorGradient: 'linear-gradient(180deg, #D8DDE2 0%, #C8CDD2 40%, #B8BEC4 100%)',
  floorHeightPercent: 32,
  previewGradient: 'linear-gradient(135deg, #C8A870 0%, #D8DDE2 100%)',
  floorColor: '#C8CDD2',
  wallColor: '#C8A870',
  accentColor: '#E8B840',
  ceilingColor: '#8A9BAD',
  environmentElements: [
    // Overcast sky atmosphere
    { id: 'sky-haze', x: 0, y: 0, w: 100, h: 68, color: 'rgba(200,215,225,0.15)', zIndex: 1 },
    // Industrial crane/structure in background center
    { id: 'crane-mast', x: 48, y: 5, w: 2, h: 45, color: '#6A7A82', zIndex: 2 },
    { id: 'crane-arm', x: 35, y: 5, w: 28, h: 2, color: '#6A7A82', zIndex: 2 },
    { id: 'crane-cable-1', x: 48, y: 7, w: 0.5, h: 20, color: '#5A6A72', opacity: 0.6, zIndex: 2 },
    { id: 'crane-cable-2', x: 44, y: 7, w: 0.5, h: 25, color: '#5A6A72', opacity: 0.5, zIndex: 2 },
    // Background industrial structure
    { id: 'bg-structure', x: 52, y: 15, w: 22, h: 40, color: '#7A8890', zIndex: 2 },
    { id: 'bg-structure-window', x: 55, y: 20, w: 8, h: 12, color: '#5A6870', zIndex: 3 },
    { id: 'bg-structure-window-2', x: 65, y: 20, w: 6, h: 10, color: '#5A6870', zIndex: 3 },
    // Yellow container (right side, background)
    { id: 'yellow-container-bg', x: 72, y: 20, w: 22, h: 48, color: '#D4A820', zIndex: 3 },
    { id: 'yellow-container-stripe', x: 72, y: 22, w: 22, h: 3, color: '#1A1A1A', opacity: 0.4, zIndex: 4 },
    { id: 'yellow-container-stripe-2', x: 72, y: 60, w: 22, h: 3, color: '#1A1A1A', opacity: 0.4, zIndex: 4 },
    { id: 'yellow-kng-text', x: 76, y: 35, w: 10, h: 8, color: '#B89010', zIndex: 4 },
    // White/grey crates center-right
    { id: 'white-crate-1', x: 58, y: 42, w: 12, h: 14, color: '#D8D8D8', zIndex: 4 },
    { id: 'white-crate-2', x: 62, y: 36, w: 10, h: 10, color: '#C8C8C8', zIndex: 5 },
    // Snow on ground
    { id: 'snow-ground', x: 0, y: 68, w: 100, h: 32, color: 'linear-gradient(180deg, #D8DDE2 0%, #C8CDD2 100%)', zIndex: 1 },
    { id: 'snow-shadow', x: 0, y: 68, w: 100, h: 4, color: 'rgba(0,0,0,0.08)', zIndex: 2 },
    // Ground line detail
    { id: 'ground-line', x: 0, y: 68, w: 100, h: 0.5, color: 'rgba(0,0,0,0.12)', zIndex: 5 },
  ],
  wallElements: [
    // Left wall — KNG beige/orange container stack
    // Wall right edge aligns with target left edge: calc(50% - 26px)
    // (target is positioned at left:50%, translateX(-26px) so left edge = 50%-26px)
    { id: 'left-wall-base', left: 0, top: 0, w: 0, wCalc: 'calc(50% - 28px)', h: 100, color: '#C8A870', zIndex: 5 },
    // KNG container face — large beige container
    { id: 'kng-container-main', left: 2, top: 5, w: 34, h: 60, color: '#D4B880', zIndex: 6 },
    { id: 'kng-container-border', left: 2, top: 5, w: 34, h: 60, color: 'rgba(0,0,0,0.15)', zIndex: 7, radius: 1 },
    // KNG text area
    { id: 'kng-label-bg', left: 8, top: 12, w: 20, h: 28, color: '#C8A860', zIndex: 7 },
    // Orange accent container (lower)
    { id: 'orange-container', left: 4, top: 55, w: 30, h: 30, color: '#E8904A', zIndex: 6 },
    { id: 'orange-container-stripe', left: 4, top: 57, w: 30, h: 2, color: 'rgba(0,0,0,0.2)', zIndex: 7 },
    { id: 'orange-container-stripe-2', left: 4, top: 80, w: 30, h: 2, color: 'rgba(0,0,0,0.2)', zIndex: 7 },
    // Container edge/corner — flush with target left edge
    { id: 'container-edge', left: 0, top: 0, w: 0, wCalc: 'calc(50% - 26px)', h: 100, color: '#8A7040', zIndex: 8 },
    { id: 'container-shadow', left: 0, top: 0, w: 0, wCalc: 'calc(50% - 24px)', h: 100, color: 'rgba(0,0,0,0.3)', zIndex: 7 },
    // Hazard stripes on container edge
    { id: 'hazard-stripe-1', left: 3, top: 65, w: 4, h: 4, color: '#1A1A1A', opacity: 0.5, zIndex: 8 },
    { id: 'hazard-stripe-2', left: 7, top: 65, w: 4, h: 4, color: '#E8904A', opacity: 0.8, zIndex: 8 },
    { id: 'hazard-stripe-3', left: 11, top: 65, w: 4, h: 4, color: '#1A1A1A', opacity: 0.5, zIndex: 8 },
    { id: 'hazard-stripe-4', left: 15, top: 65, w: 4, h: 4, color: '#E8904A', opacity: 0.8, zIndex: 8 },
    // Container number markings
    { id: 'number-8', left: 6, top: 42, w: 3, h: 5, color: '#A08050', zIndex: 8 },
    { id: 'number-4', left: 6, top: 48, w: 3, h: 5, color: '#A08050', zIndex: 8 },
    { id: 'number-1', left: 6, top: 54, w: 3, h: 5, color: '#A08050', zIndex: 8 },
    { id: 'number-0', left: 6, top: 60, w: 3, h: 5, color: '#A08050', zIndex: 8 },
  ],
  targetHeadColor: '#D0C8B8',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A8A0A0',
  targetBodyBorder: '#787070',
  targetEyeColor: '#1A1010',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

// Insert Icebox into MAP_CONFIGS at the front
MAP_CONFIGS.unshift(ICEBOX_MAP);

// Abyss map angle configs — background images imported from asset folder
const ABYSS_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'ABYSS',
  difficulty: 'Hard',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 58,
  spawnY: 22,
  // Zero out vertical offset — image composition places the corner at head-level,
  // so the target head aligns exactly with crosshair y with no additional offset.
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #0A0A1A 0%, #1A1A2E 50%, #0A0A18 100%)',
  floorGradient: 'linear-gradient(180deg, #1A1A2A 0%, #0A0A14 100%)',
  floorHeightPercent: 28,
  previewGradient: 'linear-gradient(135deg, #0A0A1A 0%, #1A1A2E 100%)',
  floorColor: '#0A0A14',
  wallColor: '#1A1A2E',
  accentColor: '#4A4AFF',
  ceilingColor: '#050510',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8C8D8',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A0A0B0',
  targetBodyBorder: '#606070',
  targetEyeColor: '#0A0A1A',
  gunBodyColor: '#1A1A2A',
  gunBarrelColor: '#0A0A1A',
  gunAccentColor: '#4A4AFF',
  gunDetailColor: '#8A8AFF',
};

MAP_CONFIGS.push(
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle1', name: 'Abyss Angle 1', siteName: 'A Site', description: 'Abyss A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_152116-1783001993338.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle2', name: 'Abyss Angle 2', siteName: 'A Site', description: 'Abyss A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_152149-1783001993500.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle3', name: 'Abyss Angle 3', siteName: 'A Site', description: 'Abyss A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_152306-1783001992320.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle4', name: 'Abyss Angle 4', siteName: 'B Site', description: 'Abyss B Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_152344-1783001992937.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle5', name: 'Abyss Angle 5', siteName: 'B Site', description: 'Abyss B Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_152837-1783001993392.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle6', name: 'Abyss Angle 6', siteName: 'B Site', description: 'Abyss B Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_152927-1783002367789.png' },
  { ...ABYSS_ANGLE_BASE, id: 'map-abyss-angle7', name: 'Abyss Angle 7', siteName: 'B Site', description: 'Abyss B Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_153021-1783002367098.png' },
);

// Ascent map angle configs — background images imported from asset folder
const ASCENT_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'ASCENT',
  difficulty: 'Medium',
  targetStyle: 'Slim',
  windowMs: 500,
  spawnSide: 'left',
  wallSide: 'left',
  spawnX: 38,
  spawnY: 20,
  // Zero out vertical offset — image composition places the corner at head-level,
  // so the target head aligns exactly with crosshair y with no additional offset.
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #7BB8E8 0%, #A8CFF0 40%, #D4E8F8 80%, #EEF5FC 100%)',
  floorGradient: 'linear-gradient(180deg, #7A7060 0%, #5A5248 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #C4865A 0%, #7A7060 100%)',
  floorColor: '#5A5248',
  wallColor: '#C4865A',
  accentColor: '#8A6040',
  ceilingColor: '#7A5030',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#1A1205',
  gunBarrelColor: '#0A0800',
  gunAccentColor: '#C8A020',
  gunDetailColor: '#FFD040',
};

MAP_CONFIGS.push(
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle1', name: 'Ascent Angle 1', siteName: 'B Site', description: 'Ascent B Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_153646-1783002378317.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle2', name: 'Ascent Angle 2', siteName: 'B Site', description: 'Ascent B Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_153722-1783002378048.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle3', name: 'Ascent Angle 3', siteName: 'B Site', description: 'Ascent B Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_153749-1783002378600.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle4', name: 'Ascent Angle 4', siteName: 'B Site', description: 'Ascent B Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_153837-1783002414030.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle5', name: 'Ascent Angle 5', siteName: 'B Site', description: 'Ascent B Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_153854-1783002412681.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle6', name: 'Ascent Angle 6', siteName: 'B Site', description: 'Ascent B Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_153909-1783002412671.png' },
  { ...ASCENT_ANGLE_BASE, id: 'map-ascent-angle7', name: 'Ascent Angle 7', siteName: 'B Site', description: 'Ascent B Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_153925-1783002417529.png' },
);

// Bind map angle configs
const BIND_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'BIND',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'left',
  wallSide: 'left',
  spawnX: 38,
  spawnY: 20,
  // Zero out vertical offset — image composition places the corner at head-level.
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #C8A060 0%, #E8C080 40%, #F0D090 100%)',
  floorGradient: 'linear-gradient(180deg, #8A7050 0%, #6A5030 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #C8A060 0%, #8A7050 100%)',
  floorColor: '#6A5030',
  wallColor: '#C8A060',
  accentColor: '#E8A030',
  ceilingColor: '#7A5020',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#1A1205',
  gunBarrelColor: '#0A0800',
  gunAccentColor: '#C8A020',
  gunDetailColor: '#FFD040',
};

MAP_CONFIGS.push(
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle1', name: 'Bind Angle 1', siteName: 'A Site', description: 'Bind A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_154132-1783002418625.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle2', name: 'Bind Angle 2', siteName: 'A Site', description: 'Bind A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_154149-1783003258009.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle3', name: 'Bind Angle 3', siteName: 'A Site', description: 'Bind A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_154207-1783003258011.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle4', name: 'Bind Angle 4', siteName: 'A Site', description: 'Bind A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_154235-1783003258130.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle5', name: 'Bind Angle 5', siteName: 'A Site', description: 'Bind A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_154254-1783003258129.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle6', name: 'Bind Angle 6', siteName: 'A Site', description: 'Bind A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_154314-1783003258138.png' },
  { ...BIND_ANGLE_BASE, id: 'map-bind-angle7', name: 'Bind Angle 7', siteName: 'A Site', description: 'Bind A Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_154328-1783003263606.png' },
);

// Breeze map angle configs
const BREEZE_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'BREEZE',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'left',
  wallSide: 'left',
  spawnX: 38,
  spawnY: 20,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #4A8AB8 0%, #6AAAD8 40%, #8ACAF0 80%, #B0DCF8 100%)',
  floorGradient: 'linear-gradient(180deg, #7A9A7A 0%, #5A7A5A 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #4A8AB8 0%, #7A9A7A 100%)',
  floorColor: '#5A7A5A',
  wallColor: '#C8B890',
  accentColor: '#60A0C0',
  ceilingColor: '#3A6A8A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#1A1205',
  gunBarrelColor: '#0A0800',
  gunAccentColor: '#60A0C0',
  gunDetailColor: '#90D0F0',
};

MAP_CONFIGS.push(
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle1', name: 'Breeze Angle 1', siteName: 'A Site', description: 'Breeze A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_154507-1783003278474.png' },
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle2', name: 'Breeze Angle 2', siteName: 'A Site', description: 'Breeze A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_154525-1783003278461.png' },
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle3', name: 'Breeze Angle 3', siteName: 'A Site', description: 'Breeze A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_154558-1783003278418.png' },
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle4', name: 'Breeze Angle 4', siteName: 'A Site', description: 'Breeze A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_154613-1783003278498.png' },
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle5', name: 'Breeze Angle 5', siteName: 'A Site', description: 'Breeze A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_154626-1783003409963.png' },
  { ...BREEZE_ANGLE_BASE, id: 'map-breeze-angle6', name: 'Breeze Angle 6', siteName: 'A Site', description: 'Breeze A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_154650-1783003409972.png' },
);

// Corrode map angle configs
const CORRODE_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'CORRODE',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'left',
  wallSide: 'left',
  spawnX: 38,
  spawnY: 20,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #2A3A2A 0%, #3A4A3A 40%, #4A5A4A 80%, #5A6A5A 100%)',
  floorGradient: 'linear-gradient(180deg, #4A4A3A 0%, #3A3A2A 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #2A3A2A 0%, #4A4A3A 100%)',
  floorColor: '#3A3A2A',
  wallColor: '#6A7A5A',
  accentColor: '#8AAA60',
  ceilingColor: '#2A3A1A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#1A1205',
  gunBarrelColor: '#0A0800',
  gunAccentColor: '#8AAA60',
  gunDetailColor: '#AACA80',
};

MAP_CONFIGS.push(
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle1', name: 'Corrode Angle 1', siteName: 'A Site', description: 'Corrode A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_154852-1783003419631.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle2', name: 'Corrode Angle 2', siteName: 'A Site', description: 'Corrode A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_154908-1783003419663.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle3', name: 'Corrode Angle 3', siteName: 'A Site', description: 'Corrode A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_154925-1783003419572.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle4', name: 'Corrode Angle 4', siteName: 'A Site', description: 'Corrode A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_154959-1783009638065.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle5', name: 'Corrode Angle 5', siteName: 'A Site', description: 'Corrode A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_155016-1783009638032.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle6', name: 'Corrode Angle 6', siteName: 'A Site', description: 'Corrode A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_155044-1783009638062.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle7', name: 'Corrode Angle 7', siteName: 'A Site', description: 'Corrode A Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_155102-1783009638042.png' },
  { ...CORRODE_ANGLE_BASE, id: 'map-corrode-angle8', name: 'Corrode Angle 8', siteName: 'A Site', description: 'Corrode A Site — Angle 8', backgroundImage: '/assets/images/Screenshot_2026-07-02_155129-1783009638043.png' },
);

// Fracture map angle configs
const FRACTURE_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'FRACTURE',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 550,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 58,
  spawnY: 22,
  spawnYOffsetPx: 38,
  crosshairOffsetX: 0,
  crosshairOffsetY: 9,
  skyGradient: 'linear-gradient(180deg, #1A1A2E 0%, #2A2A3E 100%)',
  floorGradient: 'linear-gradient(180deg, #3A3A4A 0%, #2A2A38 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #2A2A3E 0%, #3A3A4A 100%)',
  floorColor: '#2A2A38',
  wallColor: '#3A3A4A',
  accentColor: '#FF4655',
  ceilingColor: '#1A1A2E',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...FRACTURE_ANGLE_BASE, id: 'map-fracture-angle1', name: 'Fracture Angle 1', siteName: 'A Site', description: 'Fracture A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_155328-1783009655000.png' },
  { ...FRACTURE_ANGLE_BASE, id: 'map-fracture-angle2', name: 'Fracture Angle 2', siteName: 'A Site', description: 'Fracture A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_155351-1783009655208.png' },
  { ...FRACTURE_ANGLE_BASE, id: 'map-fracture-angle3', name: 'Fracture Angle 3', siteName: 'A Site', description: 'Fracture A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_155427-1783009655221.png' },
  { ...FRACTURE_ANGLE_BASE, id: 'map-fracture-angle4', name: 'Fracture Angle 4', siteName: 'A Site', description: 'Fracture A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_155454-1783009655221.png' },
  { ...FRACTURE_ANGLE_BASE, id: 'map-fracture-angle5', name: 'Fracture Angle 5', siteName: 'A Site', description: 'Fracture A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_155514-1783009655205.png' },
);

// Haven map angle configs
const HAVEN_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'HAVEN',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 550,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 60,
  spawnY: 22,
  spawnYOffsetPx: 38,
  crosshairOffsetX: 0,
  crosshairOffsetY: 10,
  skyGradient: 'linear-gradient(180deg, #4A6A8A 0%, #6A8AAA 50%, #8AAACA 100%)',
  floorGradient: 'linear-gradient(180deg, #5A5A4A 0%, #3A3A2A 100%)',
  floorHeightPercent: 28,
  previewGradient: 'linear-gradient(135deg, #4A6A8A 0%, #5A5A4A 100%)',
  floorColor: '#3A3A2A',
  wallColor: '#8A9AAA',
  accentColor: '#AABBCC',
  ceilingColor: '#3A4A5A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle1', name: 'Haven Angle 1', siteName: 'A Site', description: 'Haven A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_155724-1783009908084.png' },
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle2', name: 'Haven Angle 2', siteName: 'A Site', description: 'Haven A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_155739-1783009908073.png' },
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle3', name: 'Haven Angle 3', siteName: 'A Site', description: 'Haven A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_155754-1783009908065.png' },
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle4', name: 'Haven Angle 4', siteName: 'A Site', description: 'Haven A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_155817-1783009908067.png' },
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle5', name: 'Haven Angle 5', siteName: 'A Site', description: 'Haven A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_155834-1783009908055.png' },
  { ...HAVEN_ANGLE_BASE, id: 'map-haven-angle6', name: 'Haven Angle 6', siteName: 'A Site', description: 'Haven A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_155852-1783011994316.png' },
);

// Icebox map angle configs
const ICEBOX_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'ICEBOX',
  difficulty: 'Hard',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 60,
  spawnY: 22,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #8A9BAD 0%, #A8B8C8 35%, #C4D0D8 65%, #D8E0E6 100%)',
  floorGradient: 'linear-gradient(180deg, #D8DDE2 0%, #C8CDD2 40%, #B8BEC4 100%)',
  floorHeightPercent: 32,
  previewGradient: 'linear-gradient(135deg, #C8A870 0%, #D8DDE2 100%)',
  floorColor: '#C8CDD2',
  wallColor: '#C8A870',
  accentColor: '#E8B840',
  ceilingColor: '#8A9BAD',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#D0C8B8',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A8A0A0',
  targetBodyBorder: '#787070',
  targetEyeColor: '#1A1010',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle1', name: 'Icebox Angle 1', siteName: 'B Site', description: 'Icebox B Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_160052-1783012280932.png' },
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle2', name: 'Icebox Angle 2', siteName: 'B Site', description: 'Icebox B Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_160109-1783012281075.png' },
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle3', name: 'Icebox Angle 3', siteName: 'B Site', description: 'Icebox B Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_160125-1783012281035.png' },
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle4', name: 'Icebox Angle 4', siteName: 'B Site', description: 'Icebox B Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_160146-1783012281087.png' },
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle5', name: 'Icebox Angle 5', siteName: 'B Site', description: 'Icebox B Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_160212-1783012281097.png' },
  { ...ICEBOX_ANGLE_BASE, id: 'map-icebox-angle6', name: 'Icebox Angle 6', siteName: 'B Site', description: 'Icebox B Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_160230-1783012530317.png' },
);

// (Duplicate Abyss block removed — Abyss angles are already defined above via ABYSS_ANGLE_BASE)

// Lotus map angle configs
const LOTUS_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'LOTUS',
  difficulty: 'Hard',
  targetStyle: 'Fast',
  windowMs: 450,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 60,
  spawnY: 22,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #1A1A2E 0%, #2A2A4E 50%, #3A2A3E 100%)',
  floorGradient: 'linear-gradient(180deg, #4A4A5A 0%, #2A2A38 100%)',
  floorHeightPercent: 32,
  previewGradient: 'linear-gradient(135deg, #2A2A4E 0%, #4A4A5A 100%)',
  floorColor: '#2A2A38',
  wallColor: '#D4C8B0',
  accentColor: '#00CCAA',
  ceilingColor: '#6A4A2A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C0B098',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#1A1A0A',
  gunBarrelColor: '#0A0A05',
  gunAccentColor: '#C89020',
  gunDetailColor: '#AA2020',
};

MAP_CONFIGS.push(
  { ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle2', name: 'Lotus Angle 2', siteName: 'C Garage', description: 'Lotus C Garage — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_160640-1783012559583.png' },
  { ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle3', name: 'Lotus Angle 3', siteName: 'C Garage', description: 'Lotus C Garage — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_160656-1783012559697.png' },
  { ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle4', name: 'Lotus Angle 4', siteName: 'C Garage', description: 'Lotus C Garage — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_160711-1783012559682.png' },
  { ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle5', name: 'Lotus Angle 5', siteName: 'C Garage', description: 'Lotus C Garage — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_160723-1783012559568.png' },
{ ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle6', name: 'Lotus Angle 6', siteName: 'C Garage', description: 'Lotus C Garage — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_160459-1783012689318.png' },
{ ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle7', name: 'Lotus Angle 7', siteName: 'C Garage', description: 'Lotus C Garage — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_160520-1783012689429.png' },
{ ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle8', name: 'Lotus Angle 8', siteName: 'C Garage', description: 'Lotus C Garage — Angle 8', backgroundImage: '/assets/images/Screenshot_2026-07-02_160539-1783012689326.png' },
{ ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle9', name: 'Lotus Angle 9', siteName: 'C Garage', description: 'Lotus C Garage — Angle 9', backgroundImage: '/assets/images/Screenshot_2026-07-02_160550-1783012689416.png' },
{ ...LOTUS_ANGLE_BASE, id: 'map-lotus-angle10', name: 'Lotus Angle 10', siteName: 'C Garage', description: 'Lotus C Garage — Angle 10', backgroundImage: '/assets/images/Screenshot_2026-07-02_160607-1783012689426.png' },
);

// Pearl map angle configs
const PEARL_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'PEARL',
  difficulty: 'Medium',
  targetStyle: 'Precise',
  windowMs: 500,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 60,
  spawnY: 22,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #1A3A5A 0%, #2A4A6A 30%, #3A5A7A 60%, #4A6A8A 100%)',
  floorGradient: 'linear-gradient(180deg, #5A6A7A 0%, #3A4A5A 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #1A3A5A 0%, #5A6A7A 100%)',
  floorColor: '#3A4A5A',
  wallColor: '#8A9AA0',
  accentColor: '#4A8ACA',
  ceilingColor: '#2A3A4A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#B8C8D0',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#8A9AA0',
  targetBodyBorder: '#5A6A70',
  targetEyeColor: '#0A1A2A',
  gunBodyColor: '#1A2A3A',
  gunBarrelColor: '#0A1A2A',
  gunAccentColor: '#2A5A9A',
  gunDetailColor: '#4A8ACA',
};

MAP_CONFIGS.push(
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle1', name: 'Pearl Angle 1', siteName: 'A Site', description: 'Pearl A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_160914-1783012834990.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle2', name: 'Pearl Angle 2', siteName: 'A Site', description: 'Pearl A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_160931-1783012834896.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle3', name: 'Pearl Angle 3', siteName: 'A Site', description: 'Pearl A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_160947-1783012835030.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle4', name: 'Pearl Angle 4', siteName: 'A Site', description: 'Pearl A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_161002-1783012835036.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle5', name: 'Pearl Angle 5', siteName: 'A Site', description: 'Pearl A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_161017-1783012835015.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle6', name: 'Pearl Angle 6', siteName: 'A Site', description: 'Pearl A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_161039-1783013057954.png' },
  { ...PEARL_ANGLE_BASE, id: 'map-pearl-angle7', name: 'Pearl Angle 7', siteName: 'A Site', description: 'Pearl A Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_161055-1783013057945.png' },
);

// Sunset map angle configs
const SUNSET_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'SUNSET',
  difficulty: 'Easy',
  targetStyle: 'Standard',
  windowMs: 600,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 58,
  spawnY: 22,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #FF8C42 0%, #FFB347 30%, #FFD580 60%, #FFF4CC 100%)',
  floorGradient: 'linear-gradient(180deg, #C4A882 0%, #B89060 100%)',
  floorHeightPercent: 28,
  previewGradient: 'linear-gradient(135deg, #FF8C42 0%, #C4A882 100%)',
  floorColor: '#B89060',
  wallColor: '#C4A882',
  accentColor: '#E87C3E',
  ceilingColor: '#8B6340',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#D4C4B0',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#B8A090',
  targetBodyBorder: '#8A7060',
  targetEyeColor: '#2A1A0A',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...SUNSET_ANGLE_BASE, id: 'map-sunset-angle1', name: 'Sunset Angle 1', siteName: 'B Site', description: 'Sunset B Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_162113-1783012008522.png' },
  { ...SUNSET_ANGLE_BASE, id: 'map-sunset-angle2', name: 'Sunset Angle 2', siteName: 'B Site', description: 'Sunset B Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_162129-1783012008508.png' },
  { ...SUNSET_ANGLE_BASE, id: 'map-sunset-angle3', name: 'Sunset Angle 3', siteName: 'B Site', description: 'Sunset B Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_162143-1783012008528.png' },
  { ...SUNSET_ANGLE_BASE, id: 'map-sunset-angle4', name: 'Sunset Angle 4', siteName: 'B Site', description: 'Sunset B Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_162212-1783012008516.png' },
  { ...SUNSET_ANGLE_BASE, id: 'map-sunset-angle5', name: 'Sunset Angle 5', siteName: 'B Site', description: 'Sunset B Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_162227-1783012091910.png' },
);

// Split map angle configs
const SPLIT_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'SPLIT',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 500,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 60,
  spawnY: 22,
  spawnYOffsetPx: 0,
  crosshairOffsetX: 0,
  crosshairOffsetY: 0,
  skyGradient: 'linear-gradient(180deg, #2A2A3A 0%, #3A3A4A 50%, #4A4A5A 100%)',
  floorGradient: 'linear-gradient(180deg, #4A4A5A 0%, #3A3A4A 100%)',
  floorHeightPercent: 30,
  previewGradient: 'linear-gradient(135deg, #2A2A3A 0%, #4A4A5A 100%)',
  floorColor: '#3A3A4A',
  wallColor: '#8A8A9A',
  accentColor: '#FF4655',
  ceilingColor: '#1A1A2A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C8B89A',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle1', name: 'Split Angle 1', siteName: 'A Site', description: 'Split A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_161417-1783013067042.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle2', name: 'Split Angle 2', siteName: 'A Site', description: 'Split A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_161434-1783013067052.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle3', name: 'Split Angle 3', siteName: 'A Site', description: 'Split A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_161451-1783013067003.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle4', name: 'Split Angle 4', siteName: 'A Site', description: 'Split A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_161236-1783013225588.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle5', name: 'Split Angle 5', siteName: 'A Site', description: 'Split A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_161254-1783013225703.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle6', name: 'Split Angle 6', siteName: 'A Site', description: 'Split A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_161320-1783013225682.png' },
  { ...SPLIT_ANGLE_BASE, id: 'map-split-angle7', name: 'Split Angle 7', siteName: 'A Site', description: 'Split A Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_161339-1783013225624.png' },
);

// Summit map angle configs
const SUMMIT_ANGLE_BASE: Omit<MapConfig, 'id' | 'name' | 'siteName' | 'description' | 'backgroundImage'> = {
  mapCode: 'SUMMIT',
  difficulty: 'Medium',
  targetStyle: 'Standard',
  windowMs: 550,
  spawnSide: 'right',
  wallSide: 'right',
  spawnX: 58,
  spawnY: 22,
  spawnYOffsetPx: 38,
  crosshairOffsetX: 1,
  crosshairOffsetY: 10,
  skyGradient: 'linear-gradient(180deg, #1A2A3A 0%, #2A3A4A 50%, #3A4A5A 100%)',
  floorGradient: 'linear-gradient(180deg, #4A5A6A 0%, #2A3A4A 100%)',
  floorHeightPercent: 28,
  previewGradient: 'linear-gradient(135deg, #1A2A3A 0%, #4A5A6A 100%)',
  floorColor: '#2A3A4A',
  wallColor: '#3A4A5A',
  accentColor: '#5A8AAA',
  ceilingColor: '#1A2A3A',
  environmentElements: [],
  wallElements: [],
  targetHeadColor: '#C0B098',
  targetHeadBorder: '#FF4655',
  targetBodyColor: '#A09080',
  targetBodyBorder: '#706050',
  targetEyeColor: '#1A0A00',
  gunBodyColor: '#2A2A2A',
  gunBarrelColor: '#1A1A1A',
  gunAccentColor: '#3A3A3A',
  gunDetailColor: '#555555',
};

MAP_CONFIGS.push(
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle1', name: 'Summit Angle 1', siteName: 'A Site', description: 'Summit A Site — Angle 1', backgroundImage: '/assets/images/Screenshot_2026-07-02_161704-1783013561900.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle2', name: 'Summit Angle 2', siteName: 'A Site', description: 'Summit A Site — Angle 2', backgroundImage: '/assets/images/Screenshot_2026-07-02_161733-1783013561873.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle3', name: 'Summit Angle 3', siteName: 'A Site', description: 'Summit A Site — Angle 3', backgroundImage: '/assets/images/Screenshot_2026-07-02_161751-1783013561894.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle4', name: 'Summit Angle 4', siteName: 'A Site', description: 'Summit A Site — Angle 4', backgroundImage: '/assets/images/Screenshot_2026-07-02_161810-1783013561888.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle5', name: 'Summit Angle 5', siteName: 'A Site', description: 'Summit A Site — Angle 5', backgroundImage: '/assets/images/Screenshot_2026-07-02_161832-1783013561912.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle6', name: 'Summit Angle 6', siteName: 'A Site', description: 'Summit A Site — Angle 6', backgroundImage: '/assets/images/Screenshot_2026-07-02_161844-1783013832889.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle7', name: 'Summit Angle 7', siteName: 'A Site', description: 'Summit A Site — Angle 7', backgroundImage: '/assets/images/Screenshot_2026-07-02_161901-1783013832930.png' },
  { ...SUMMIT_ANGLE_BASE, id: 'map-summit-angle8', name: 'Summit Angle 8', siteName: 'A Site', description: 'Summit A Site — Angle 8', backgroundImage: '/assets/images/Screenshot_2026-07-02_161915-1783013832896.png' },
);